// src/components/settings/CalendarSettings.tsx (LOGIC FIXED)
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  calendarDetails,
  connectGoogleCalendar,
  disconnectGoogleCalendar,
  fetchBusinessCalendars,
  updateBusinessCalendar,
  type BusinessCalendar,
  type CalendarProvider,
} from "../../api/googlecalendar";
import {
  connectMicrosoftCalendar,
  disconnectMicrosoftCalendar,
} from "../../api/microsoftcalendar";

import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
  CircularProgress,
  Checkbox,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Alert,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

type CalendarSettingsProps = {
  calendarConnected: boolean;
  calendarProvider: CalendarProvider;
  setCalendarConnected: React.Dispatch<React.SetStateAction<boolean>>;
  setCalendarProvider: React.Dispatch<React.SetStateAction<CalendarProvider>>;
  initialError?: string | null;
};

type CalendarErrorPayload = {
  error?: unknown;
  message?: unknown;
  response?: {
    data?: CalendarErrorPayload;
  };
};

const humanizeErrorCode = (code: string) =>
  code
    .replace(/_/g, " ")
    .replace(/^\w/, (letter) => letter.toUpperCase());

const getCalendarErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (error && typeof error === "object") {
    const payload = error as CalendarErrorPayload;
    const data = payload.response?.data ?? payload;

    if (typeof data.message === "string" && data.message.trim()) {
      return data.message;
    }

    if (typeof data.error === "string" && data.error.trim()) {
      return humanizeErrorCode(data.error);
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

const CalendarSettings = ({
  calendarConnected,
  calendarProvider,
  setCalendarConnected,
  setCalendarProvider,
  initialError,
}: CalendarSettingsProps) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [calLoading, setCalLoading] = useState(false);
  const [calendars, setCalendars] = useState<BusinessCalendar[]>([]);
  const [error, setError] = useState<string | null>(null);

  const connectedLabel = useMemo(() => {
    if (!calendarProvider) return "Connect Calendar";
    if (calendarConnected) {
      const name = calendarProvider === "google" ? "Google" : "Microsoft";
      return `Disconnect ${name} Calendar`;
    }
    const name = calendarProvider === "google" ? "Google" : "Microsoft";
    return `Connect ${name} Calendar`;
  }, [calendarConnected, calendarProvider]);

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  // 🔥 Load connection status
  useEffect(() => {
    if (!currentUser) return;

    (async () => {
      try {
        const data = await calendarDetails();   // ✅ NO TOKEN PASSED
        setCalendarConnected(!!data.calendar_connected);
        setCalendarProvider((data.calendar_provider ?? null) as CalendarProvider);
      } catch (e: unknown) {
        setError(getCalendarErrorMessage(e, "Failed to load calendar connection status"));
      }
    })();
  }, [currentUser, setCalendarConnected, setCalendarProvider]);

  // 🔥 Load calendars
  const loadCalendars = useCallback(async () => {
    if (!currentUser) return;

    setCalLoading(true);
    setError(null);

    try {
      const list = await fetchBusinessCalendars(); // ✅ NO TOKEN
      list.sort((a, b) => {
        if (a.is_default && !b.is_default) return -1;
        if (!a.is_default && b.is_default) return 1;
        if (a.is_bookable && !b.is_bookable) return -1;
        if (!a.is_bookable && b.is_bookable) return 1;
        return a.calendar_name.localeCompare(b.calendar_name);
      });
      setCalendars(list);
    } catch (e: unknown) {
      setError(getCalendarErrorMessage(e, "Failed to load calendars"));
    } finally {
      setCalLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (calendarConnected) loadCalendars();
    else setCalendars([]);
  }, [calendarConnected, loadCalendars]);

  // 🔧 Provider dropdown (reset provider)
  const handleProviderChange = async (event: SelectChangeEvent) => {
    const selectedProvider = event.target.value as CalendarProvider;

    setCalendarProvider(selectedProvider);
    setCalendars([]);
    setError(null);

    if (!selectedProvider) return;

    try {
      await calendarDetails({
        provider: selectedProvider,
        connected: false,
      }); // ✅ NO token
    } catch (e: unknown) {
      setError(getCalendarErrorMessage(e, "Failed to set provider"));
    }
  };

  // CONNECT FLOW
  const handleConnect = () => {
    if (!calendarProvider) return;
    if (calendarProvider === "google") connectGoogleCalendar("integrations");
    if (calendarProvider === "microsoft") connectMicrosoftCalendar("integrations");
  };

  // DISCONNECT FLOW
  const handleDisconnect = async () => {
    if (!calendarProvider) return;

    setLoading(true);
    setError(null);

    try {
      if (calendarProvider === "google") {
        await disconnectGoogleCalendar();  // ✅ NO TOKEN
      } else if (calendarProvider === "microsoft") {
        await disconnectMicrosoftCalendar();  // ✅ NO TOKEN
      }

      setCalendarConnected(false);
      setCalendars([]);
    } catch (e: unknown) {
      setError(getCalendarErrorMessage(e, "Failed to disconnect"));
    } finally {
      setLoading(false);
    }
  };

  // TOGGLE BOOKABLE
  const toggleBookable = async (cal: BusinessCalendar, checked: boolean) => {
    setError(null);

    try {
      await updateBusinessCalendar(cal.business_calendar_id, {
        is_bookable: checked,
      }); // ✅ FIXED ARG ORDER
      await loadCalendars();
    } catch (e: unknown) {
      setError(getCalendarErrorMessage(e, "Failed to update calendar"));
    }
  };

  // SET DEFAULT CALENDAR
  const setDefault = async (cal: BusinessCalendar) => {
    setError(null);

    try {
      await updateBusinessCalendar(cal.business_calendar_id, {
        is_default: true,
      }); // ✅ FIXED
      await loadCalendars();
    } catch (e: unknown) {
      setError(getCalendarErrorMessage(e, "Failed to update default calendar"));
    }
  };

  // UI continues…


  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Calendar Settings
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="calendar-provider-label">Calendar Provider</InputLabel>
        <Select
          labelId="calendar-provider-label"
          id="calendar-provider"
          label="Calendar Provider"
          value={calendarProvider || ""}
          onChange={handleProviderChange}
          disabled={calendarConnected || loading}
        >
          <MenuItem value="google">Google Calendar</MenuItem>
          <MenuItem value="microsoft">Microsoft Calendar</MenuItem>
        </Select>
      </FormControl>

      {calendarProvider && (
        <Button
          onClick={calendarConnected ? handleDisconnect : handleConnect}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : connectedLabel}
        </Button>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {calendarConnected && (
        <>
          <Divider sx={{ my: 3 }} />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              Calendars in your {calendarProvider} account
            </Typography>
            <Button size="small" onClick={loadCalendars} disabled={calLoading}>
              {calLoading ? "Refreshing…" : "Refresh"}
            </Button>
          </Stack>

          {calLoading ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              display="flex"
              flexWrap="wrap"
              gap={2}
              sx={{
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              {calendars.map((c) => (
                <Box
                  key={c.business_calendar_id}
                  sx={{
                    flex: "1 1 300px",
                    maxWidth: 360,
                    minWidth: 280,
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    boxShadow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    transition: "all 0.2s ease",
                    "&:hover": { boxShadow: 3 },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: c.color || "#999",
                          flexShrink: 0,
                        }}
                      />
                      <Tooltip title={c.calendar_name}>
                        <Typography
                          variant="subtitle1"
                          noWrap
                          sx={{ fontWeight: 500, flexGrow: 1 }}
                        >
                          {c.calendar_name}
                        </Typography>
                      </Tooltip>
                      {c.is_default && (
                        <Chip
                          size="small"
                          label="Default"
                          color="primary"
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </Stack>

                    <Tooltip title={c.is_default ? "Default calendar" : "Make default"}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setDefault(c)}
                          disabled={c.is_default}
                        >
                          {c.is_default ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>

                  {c.timezone && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {c.timezone}
                    </Typography>
                  )}

                  <Stack direction="row" alignItems="center" sx={{ mt: 1 }}>
                    <Checkbox
                      checked={c.is_bookable}
                      onChange={(e) => toggleBookable(c, e.target.checked)}
                    />
                    <Typography variant="body2">Bookable</Typography>
                  </Stack>

                  {/* Optional: Copy Provider ID tooltip */}
                  <Tooltip title={c.provider_calendar_id}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.provider_calendar_id}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      
    </Box>
  );
};

export default CalendarSettings;
